import React, { Dispatch, memo, SetStateAction, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import {
	Button,
	Confirm,
	GridContainer,
	Icons,
	Input,
	Modal,
	SliderCheckbox,
	SpinnerOverlay,
} from '@components/common';
import DynamicIconWrapper from '@components/common/DynamicIconWrapper/DynamicIconWrapper';
import Tooltip from '@components/common/Tooltip';
import ChangePassword from '@components/ModalComponents/ChangePassword';
import Confirm2Fa from '@components/ModalComponents/Confirm2FA/Confirm2FA';
import Logo from '@components/ModalComponents/OrganisationForm/BasicInfo/Logo';
import UploadLogo from '@components/ModalComponents/OrganisationForm/BasicInfo/UploadLogo';
import { TCreateUploadUrl } from '@components/ModalComponents/OrganisationForm/BasicInfo/UploadLogo/UploadLogo';
import PhoneNumberForm from '@components/ModalComponents/PhoneNumberForm/PhoneNumberForm';
import TwoFaPhoneContainer from '@components/routes/UserProfile/TwoFaPhoneContainer';

import { useMutateCheckPhoneNumber } from '@hooks/userProfile/useMutateCheckPhoneNumber';
import { useMutateManageUserProfile } from '@hooks/userProfile/useMutateManageUserProfile';
import { useUploadLogo } from '@hooks/useUploadLogo';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { IProfileUserFormValues, IUserProfile } from '@interfaces/user/userProfile';
import { PhoneNumber } from 'libphonenumber-js/types';

import { ESnackbarTypes } from '@enums/common';
import { EUserProfileFormValue } from '@enums/userProfile';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { MODAL_WIDTH_480, MODAL_WIDTH_580 } from '@constants/modals';
import { responseMessages } from '@constants/responseMessages';
import { profileUserFormAreas } from './constants';

import { schema } from './schema';

import styles from './ProfileUserForm.module.scss';

interface IProfileUserFormProps {
	userProfile: IUserProfile;
	setIsDirty: Dispatch<SetStateAction<boolean>>;
	onClose: () => void;
	closeEditUserProfileModal: () => void;
	isRequiredJobTitle: boolean;
}

const ProfileUserForm = ({
	userProfile,
	setIsDirty,
	onClose,
	closeEditUserProfileModal,
	isRequiredJobTitle,
}: IProfileUserFormProps) => {
	const userSchema = schema(isRequiredJobTitle);
	const dispatch = useAppDispatch();

	const [isOpenLogoModal, setIsOpenLogoModal] = useState(false);
	const [isOpenCodeModal, setIsOpenCodeModal] = useState(false);
	const [isOpenPhoneNumberModal, setIsOpenPhoneNumberModal] = useState(false);
	const [isOpenDisable2FaConfirmModal, setIsOpenDisable2FaConfirmModal] = useState(false);
	const [isOpenDisable2FaConfirmPasswordModal, setIsOpenDisable2FaConfirmPasswordModal] = useState(false);
	const [isOpenChangePasswordModal, setIsOpenChangePasswordModal] = useState(false);

	const {
		editUserProfile,
		isLoadingEditUserProfile,
	} = useMutateManageUserProfile({ onClose: closeEditUserProfileModal });

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		trigger,
	} = useForm<IProfileUserFormValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(userSchema),
		defaultValues: {
			[EUserProfileFormValue.firstName]: userProfile.firstName,
			[EUserProfileFormValue.lastName]: userProfile.lastName,
			[EUserProfileFormValue.avatarId]: userProfile.avatar?.id || '',
			[EUserProfileFormValue.jobTitle]: userProfile.jobTitle || '',
			[EUserProfileFormValue.phoneNumber]: userProfile.phoneNumber || '',
		},
	});

	const phoneNumberValue = watch(EUserProfileFormValue.phoneNumber);
	const phoneNumber: PhoneNumber = (phoneNumberValue ? `+${phoneNumberValue}` : '') as unknown as PhoneNumber;
	const is2FA = userProfile.is2FA;

	const toggleLogoModal = () => setIsOpenLogoModal(prevState => !prevState);

	const toggleChangePasswordModal = () => setIsOpenChangePasswordModal(prevState => !prevState);

	const setLogoId = (id: string) => setValue(EUserProfileFormValue.avatarId, id);

	const clearProfileLogo = () => setLogoId('');

	const openDisable2FaConfirmModal = () => {
		setIsOpenDisable2FaConfirmModal(true);
	};

	const openDisable2FaConfirmPasswordModal = () => {
		setIsOpenDisable2FaConfirmPasswordModal(true);
		setIsOpenDisable2FaConfirmModal(false);
	};

	const closeDisable2FaConfirmModals = () => {
		setIsOpenDisable2FaConfirmModal(false);
		setIsOpenDisable2FaConfirmPasswordModal(false);
	};

	const openCodeModal = () => setIsOpenCodeModal(true);

	const openPhoneNumberModal = () => setIsOpenPhoneNumberModal(true);

	const closeCodeModal = () => setIsOpenCodeModal(false);

	const onSuccessEnable2Fa = () => {
		dispatch(setSnackbar({
			message: responseMessages.enable2Fa,
			type: ESnackbarTypes.success,
		}));
	};

	const closePhoneNumberModal = () => setIsOpenPhoneNumberModal(false);

	const onChangePhoneNumber = (phone: string) => {
		const formattedPhone = phone.slice(1);

		setValue(EUserProfileFormValue.phoneNumber, formattedPhone as unknown as PhoneNumber);

		if (!isDirty) setIsDirty(true);
	};

	const {
		createUploadLogoUrl,
		progress,
		isLoadingCreateUploadLogoUrl,
	} = useUploadLogo({ setLogoId, toggleLogoModal });

	useEffect(() => setIsDirty(isDirty), [isDirty, setIsDirty]);

	const onSubmit = (values: IProfileUserFormValues) => {
		editUserProfile({
			firstName: values[EUserProfileFormValue.firstName],
			lastName: values[EUserProfileFormValue.lastName],
			avatarId: values[EUserProfileFormValue.avatarId] || null,
			jobTitle: values[EUserProfileFormValue.jobTitle],
			...is2FA ? {} : { phoneNumber: values[EUserProfileFormValue.phoneNumber] as PhoneNumber },
		});
	};

	const {
		checkPhoneNumber,
		isLoadingCheckPhoneNumber,
	} = useMutateCheckPhoneNumber({ onOpen: openCodeModal });

	const onClick2Fa = async (_id: string, checked: boolean) => {
		if (!checked) {
			openDisable2FaConfirmModal();
		} else {
			const isValid = await trigger(EUserProfileFormValue.phoneNumber);

			if (isValid) {
				checkPhoneNumber({ number: phoneNumberValue });
			}
		}
	};

	const isDisabled2Fa = is2FA ? false : (!!errors?.phoneNumber?.message || !phoneNumberValue);

	const isLoading = isLoadingEditUserProfile || isLoadingCheckPhoneNumber;

	return (
		<GridContainer
			columns={2}
			customClassName={styles.profileUserFormContainer}
			gap='15px 20px'
			gridTemplateAreas={profileUserFormAreas}
		>
			{isLoading && <SpinnerOverlay />}
			<Modal
				isOpen={isOpenDisable2FaConfirmModal}
				onClose={closeDisable2FaConfirmModals}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={closeDisable2FaConfirmModals}
					onSubmit={openDisable2FaConfirmPasswordModal}
					question='Are you sure you want to disable Two-Factor Authentication (2FA)?'
				>
					This will remove the extra layer of security for your account.
				</Confirm>
			</Modal>
			<Modal
				isOpen={isOpenDisable2FaConfirmPasswordModal}
				onClose={closeDisable2FaConfirmModals}
				title='Confirm action'
				width='400px'
			>
				<Confirm2Fa onClose={closeDisable2FaConfirmModals} />
			</Modal>
			<Modal
				isOpen={isOpenCodeModal}
				onClose={closeCodeModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<TwoFaPhoneContainer
					onClose={closeCodeModal}
					onSuccess={onSuccessEnable2Fa}
					phoneNumber={phoneNumber}
				/>
			</Modal>
			<Modal
				isOpen={isOpenPhoneNumberModal}
				onClose={closePhoneNumberModal}
				title='Change contact number'
				width='400px'
			>
				<PhoneNumberForm
					codeTitle='Confirm action'
					onClose={closePhoneNumberModal}
					prevPhoneNumber={userProfile.phoneNumber}
				/>
			</Modal>
			<Modal
				isOpen={isOpenLogoModal}
				isScrollableContent
				onClose={toggleLogoModal}
				title='Upload image'
				width={MODAL_WIDTH_580}
			>
				<UploadLogo
					createUploadUrl={createUploadLogoUrl as unknown as TCreateUploadUrl}
					isLoadingCreateUploadUrl={isLoadingCreateUploadLogoUrl}
					progress={progress}
					toggleLogoModal={toggleLogoModal}
				/>
			</Modal>
			<Modal
				isOpen={isOpenChangePasswordModal}
				onClose={toggleChangePasswordModal}
				title='Change password'
				width={is2FA ? MODAL_WIDTH_480 : '400px'}
			>
				<ChangePassword
					is2FA={is2FA}
					onClose={toggleChangePasswordModal}
					toggleChangePasswordModal={toggleChangePasswordModal}
				/>
			</Modal>
			<div className={styles.firstName}>
				<Input
					errorMessage={errors?.firstName?.message}
					label='First Name'
					name={EUserProfileFormValue.firstName}
					placeholder='Enter first name'
					register={register}
					required
					setValue={setValue}
				/>
			</div>
			<div className={styles.lastName}>
				<Input
					errorMessage={errors?.lastName?.message}
					label='Last Name'
					name={EUserProfileFormValue.lastName}
					placeholder='Enter last name'
					register={register}
					required
					setValue={setValue}
				/>
			</div>
			<div className={styles.logo}>
				<Logo
					avatarId={watch(EUserProfileFormValue.avatarId)}
					description='Avatar'
					openLogoModal={toggleLogoModal}
					removeLogo={clearProfileLogo}
				/>
			</div>
			<div className={styles.job}>
				<Input
					errorMessage={errors?.jobTitle?.message}
					label='Job Title'
					name={EUserProfileFormValue.jobTitle}
					placeholder='Enter job title'
					register={register}
					required={isRequiredJobTitle}
				/>
			</div>
			<div className={styles.email}>
				<Input
					disabled
					label='Email Address'
					name='email'
					placeholder='Enter email address'
					required
					value={userProfile.email}
					withUseForms={false}
				/>
			</div>
			<div className={styles.contactNumber}>
				<div className={styles.phoneInput}>
					<Input
						className={styles.phoneInput}
						disabled={is2FA}
						errorMessage={errors?.phoneNumber?.message}
						isPhoneNumber
						label='Contact Number'
						name={EUserProfileFormValue.phoneNumber}
						onChange={onChangePhoneNumber}
						placeholder='Enter contact number'
						required={is2FA}
						value={phoneNumber}
					/>
				</div>
				{is2FA
					&& <div className={styles.editButton}>
						<DynamicIconWrapper
							icon={Icons.editIcon}
							onClick={openPhoneNumberModal}
							tooltipLabel='Edit contact number'
						/>
					</div>}
			</div>
			<div className={styles.fa}>
				<Tooltip
					content='Enter a valid contact number to enable 2FA'
					isDisabled={!isDisabled2Fa}
				>
					<div>
						<SliderCheckbox
							checked={is2FA}
							disabled={isDisabled2Fa}
							id={EUserProfileFormValue.is2FA}
							isLabelAfter
							label='Enable Two-Factor Authentication (2FA)'
							onChange={onClick2Fa}
							tooltipText={is2FA
								? <ol>
									<li>Use the &quot;Disable 2FA&quot; option</li>
									<li>Confirm your intention to disable 2FA</li>
									<li>Enter your account password for verification</li>
								</ol>
								: <ol>
									<li>Provide your contact number</li>
									<li>Use the &quot;Enable 2FA&quot; option</li>
									<li>Enter the verification code</li>
									<li>Verify and enable 2FA</li>
								</ol>
							}
						/>
					</div>
				</Tooltip>
			</div>
			<div className={styles.password}>
				<Button
					onClick={toggleChangePasswordModal}
					secondary
					text='Change password'
				/>
			</div>
			<div className={styles.submitButtons}>
				<Button
					onClick={onClose}
					text='Cancel'
					withoutBackground
				/>
				<Button
					onClick={handleSubmit(onSubmit)}
					text='Save'
				/>
			</div>
		</GridContainer>
	);
};

export default memo(ProfileUserForm);
